import { getLocalizedString } from '~/i18n/utils.ts';
import { getRedirectsTranslationKey } from '~/pages/FullStackAuthentication/utils.ts';
import { getKbLinkTransformer } from '~/i18n';
import { externalLinks } from '~/consts.ts';
import { JSXElement } from 'solid-js';
import { getKebabCase } from '~/utils/commonUtils.ts';
import {
  ApiConfigData,
  ApplicationTypeEnum,
  getClientCredentialsKey,
  getEnvironmentMultiApplicationDetailQueryKey
} from '~/api';

enum RedirectUriFlowTypes {
  SSO_QUICK_START = 'SSO_QUICK_START',
  REDIRECTS = 'REDIRECTS',
  API_CONFIG = 'API_CONFIG',
  MULTI_APPLICATION = 'MULTI_APPLICATION',
  FSA_QUICK_START = 'FSA_QUICK_START'
}

enum RedirectUriType {
  POST_LOGIN_URI = 'POST_LOGIN_URI',
  INITIATE_LOGIN_URI = 'INITIATE_LOGIN_URI',
  POST_LOGOUT_URI = 'POST_LOGOUT_URI',
  BACK_CHANNEL_LOGOUT_URI = 'BACK_CHANNEL_LOGOUT_URI'
}

const redirectUriApiUpdateRequestKey = {
  [RedirectUriType.POST_LOGIN_URI]: 'postLoginUris',
  [RedirectUriType.INITIATE_LOGIN_URI]: 'initiateLoginUri',
  [RedirectUriType.BACK_CHANNEL_LOGOUT_URI]: 'backChannelLogoutUris',
  [RedirectUriType.POST_LOGOUT_URI]: 'postLogoutRedirectUris'
};

const getRedirectUriHeader = (type: RedirectUriType, intl: any) => {
  const redirectUriHeader = {
    [RedirectUriType.POST_LOGOUT_URI]: getLocalizedString(
      getRedirectsTranslationKey('post_logout_uri_header'),
      { intl }
    ),
    [RedirectUriType.BACK_CHANNEL_LOGOUT_URI]: getLocalizedString(
      getRedirectsTranslationKey('back_channel_logout_uri_header'),
      { intl }
    ),
    [RedirectUriType.POST_LOGIN_URI]: getLocalizedString(
      getRedirectsTranslationKey('post_login_uri_header'),
      { intl }
    ),
    [RedirectUriType.INITIATE_LOGIN_URI]: getLocalizedString(
      getRedirectsTranslationKey('initiate_login_uri_header'),
      { intl }
    )
  };
  return redirectUriHeader[type];
};

const getRedirectUriSubheader = (type: RedirectUriType, intl: any) => {
  const redirectUriSubheader = {
    [RedirectUriType.POST_LOGOUT_URI]: getLocalizedString(
      getRedirectsTranslationKey('post_logout_uri_subheader'),
      {
        intl,
        kbLink: getKbLinkTransformer(
          externalLinks.kbArticles.postLogoutUrls,
          getKebabCase(RedirectUriType.POST_LOGOUT_URI.toLowerCase()) +
            '-learn-more'
        )
      }
    ),
    [RedirectUriType.BACK_CHANNEL_LOGOUT_URI]: getLocalizedString(
      getRedirectsTranslationKey('back_channel_logout_uri_subheader'),
      {
        intl,
        kbLink: getKbLinkTransformer(
          externalLinks.kbArticles.backChannelLogoutUrls,
          getKebabCase(RedirectUriType.BACK_CHANNEL_LOGOUT_URI.toLowerCase()) +
            '-learn-more'
        )
      }
    ),
    [RedirectUriType.POST_LOGIN_URI]: getLocalizedString(
      getRedirectsTranslationKey('post_login_uri_subheader'),
      {
        intl,
        kbLink: getKbLinkTransformer(
          externalLinks.kbArticles.allowedCallbackUrls,
          getKebabCase(RedirectUriType.POST_LOGIN_URI.toLowerCase()) +
            '-learn-more'
        )
      }
    ),
    [RedirectUriType.INITIATE_LOGIN_URI]: getLocalizedString(
      getRedirectsTranslationKey('initiate_login_uri_subheader'),
      {
        intl,
        kbLink: getKbLinkTransformer(
          externalLinks.kbArticles.initiateLoginUrl,
          getKebabCase(RedirectUriType.INITIATE_LOGIN_URI.toLowerCase()) +
            '-learn-more'
        ),
        codeBlock: (str: string): JSXElement => (
          <span
            class={`font-code text-[#686868] bg-[#F1F1F1] py-[2px] px-[6px] rounded-sm text-xs`}
          >
            {str}
          </span>
        )
      }
    )
  };
  return redirectUriSubheader[type];
};

const getDeleteConfirmationParamsForRedirectUri = (
  type: RedirectUriType,
  intl: any
) => ({
  title: getLocalizedString(
    getRedirectsTranslationKey('delete_redirect_uri_title'),
    {
      intl,
      title: getRedirectUriHeader(type, intl).replace(/s$/, '')
    }
  ),
  description: getLocalizedString(
    getRedirectsTranslationKey('delete_redirect_uri_confirm_message'),
    {
      intl,
      title: getRedirectUriHeader(type, intl).toLowerCase().replace(/s$/, '')
    }
  ),
  ctaLabel: getLocalizedString('cta_labels.delete', { intl })
});

const generateQueryKeysForRefetching = (flowType?: RedirectUriFlowTypes) => {
  const isQuickStartFlow =
    flowType === RedirectUriFlowTypes.SSO_QUICK_START ||
    flowType === RedirectUriFlowTypes.FSA_QUICK_START;
  switch (flowType) {
    case RedirectUriFlowTypes.MULTI_APPLICATION:
      return getEnvironmentMultiApplicationDetailQueryKey();
    default:
      return getClientCredentialsKey(isQuickStartFlow);
  }
};

const generateURLBlockFromFlowType = (
  flowType?: RedirectUriFlowTypes,
  data?: ApiConfigData
) => {
  switch (flowType) {
    case RedirectUriFlowTypes.SSO_QUICK_START:
      return [RedirectUriType.POST_LOGIN_URI];
    case RedirectUriFlowTypes.FSA_QUICK_START:
      return [
        RedirectUriType.POST_LOGIN_URI,
        RedirectUriType.INITIATE_LOGIN_URI
      ];
    case RedirectUriFlowTypes.MULTI_APPLICATION:
      if (data) {
        return data.clientType === ApplicationTypeEnum.DEFAULT_APPLICATION ||
          data.clientType === ApplicationTypeEnum.WEB_APPLICATION
          ? Object.values(RedirectUriType)
          : [
              RedirectUriType.POST_LOGIN_URI,
              RedirectUriType.INITIATE_LOGIN_URI,
              RedirectUriType.POST_LOGOUT_URI
            ];
      } else {
        return [];
      }
    default:
      return Object.values(RedirectUriType);
  }
};

export {
  RedirectUriFlowTypes,
  RedirectUriType,
  redirectUriApiUpdateRequestKey,
  getRedirectUriHeader,
  getRedirectUriSubheader,
  getDeleteConfirmationParamsForRedirectUri,
  generateQueryKeysForRefetching,
  generateURLBlockFromFlowType
};
